import { createContext, useContext, useMemo, useState, type PropsWithChildren } from 'react'
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material'

interface ColorModeValue { dark: boolean; toggle: () => void }
const ColorModeContext = createContext<ColorModeValue>({ dark: false, toggle: () => undefined })

export function AppTheme({ children }: PropsWithChildren) {
  const [dark, setDark] = useState(() => localStorage.getItem('emailserver.theme') === 'dark')
  const mode = dark ? 'dark' : 'light'
  const theme = useMemo(() => createTheme({
    palette: {
      mode,
      primary: { main: dark ? '#818cf8' : '#4f46e5' },
      secondary: { main: '#06b6d4' },
      background: dark ? { default: '#070b14', paper: '#111827' } : { default: '#f5f7fb', paper: '#ffffff' },
    },
    typography: { fontFamily: '"DM Sans", sans-serif', h4: { fontWeight: 700, letterSpacing: '-0.035em' }, h5: { fontWeight: 700, letterSpacing: '-0.025em' }, h6: { fontWeight: 700 } },
    shape: { borderRadius: 14 },
    components: {
      MuiCard: { styleOverrides: { root: { backgroundImage: 'none', border: `1px solid ${dark ? '#1f2937' : '#e8ebf2'}`, boxShadow: dark ? '0 16px 50px rgba(0,0,0,.22)' : '0 10px 35px rgba(15,23,42,.045)' } } },
      MuiButton: { defaultProps: { disableElevation: true }, styleOverrides: { root: { textTransform: 'none', fontWeight: 700, borderRadius: 10 } } },
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiDialog: { styleOverrides: { paper: { backgroundImage: 'none' } } },
    },
  }), [dark, mode])
  const value = useMemo(() => ({ dark, toggle: () => setDark((current) => { localStorage.setItem('emailserver.theme', current ? 'light' : 'dark'); return !current }) }), [dark])
  return <ColorModeContext.Provider value={value}><ThemeProvider theme={theme}><CssBaseline />{children}</ThemeProvider></ColorModeContext.Provider>
}

export const useColorMode = () => useContext(ColorModeContext)
